import React, { ReactNode } from 'react';
import { Badge, Col, Row } from 'react-bootstrap';

type CompanyRankingItem = {
  companyId: string;
  companyName: string;
};

type CompanyRankingListProps<TItem extends CompanyRankingItem> = {
  items: TItem[];
  emptyText: string;
  cardClassName: string;
  metaClassName?: string;
  badgeClassName?: string;
  badgeBg: 'primary' | 'success' | 'danger';
  getBadgeText: (item: TItem) => string;
  getMetaText: (item: TItem) => ReactNode;
  onItemClick?: (item: TItem) => void;
};

type CardWrapperProps = {
  className: string;
  onClick?: () => void;
  children: ReactNode;
};

const CardWrapper = ({ className, onClick, children }: CardWrapperProps) => (
  <div className={className}>
    {onClick ? (
      <button
        type="button"
        className="d-contents cursor-pointer border-0 bg-transparent p-0 text-start w-100"
        onClick={onClick}
      >
        {children}
      </button>
    ) : (
      children
    )}
  </div>
);

export const CompanyRankingList = <TItem extends CompanyRankingItem>({
  items,
  emptyText,
  cardClassName,
  metaClassName = 'top-company-card-meta',
  badgeClassName,
  badgeBg,
  getBadgeText,
  getMetaText,
  onItemClick,
}: CompanyRankingListProps<TItem>) => {
  if (items.length === 0) {
    return <div className="text-muted">{emptyText}</div>;
  }

  return (
    <Row xs={1} md={2} xl={3} className="g-3">
      {items.map((item, idx) => (
        <Col key={item.companyId}>
          <CardWrapper
            className={`border rounded-3 p-3 h-100 d-flex flex-column gap-2 ${cardClassName}`}
            onClick={onItemClick ? () => onItemClick(item) : undefined}
          >
            <div className="d-flex justify-content-between align-items-start gap-2">
              <span className="fw-semibold">
                {idx + 1}. {item.companyName}
              </span>
              <Badge
                bg={badgeBg}
                className={`text-nowrap fs-6 px-2 py-1 ${badgeClassName || ''}`}
              >
                {getBadgeText(item)}
              </Badge>
            </div>
            <div className={`text-muted ${metaClassName}`}>
              {getMetaText(item)}
            </div>
          </CardWrapper>
        </Col>
      ))}
    </Row>
  );
};
